import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CardContent, 
  Alert, 
  CircularProgress,
  Link,
  Paper,
  Chip
} from '@mui/material';
import { LockOutlined, Email, Visibility, VisibilityOff, Business, Security } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PasswordStrengthMeter from '../../components/Auth/PasswordStrengthMeter';
import { evaluatePassword } from '../../utils/passwordStrength';

type LoginStep = 'credentials' | '2fa' | '2fa_setup' | 'backup_codes' | 'password_change';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<LoginStep>('credentials');
  const [tempToken, setTempToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenant');
  
  const { login, verify2FA, start2FASetup, complete2FASetup, changeFirstPassword, establishSessionFromToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setFormData(prev => ({ ...prev, email: emailParam }));
    }

    // SSO callback: one-time `code` (never JWTs in the query string).
    // Exchange then establishSessionFromToken (clears React Query + loads user).
    const ssoStatus = searchParams.get('sso');
    const ssoCode = searchParams.get('code');
    const ssoError = searchParams.get('sso_error');
    if (ssoError) {
      setError(`SSO login failed: ${ssoError}`);
      toast.error(`SSO login failed: ${ssoError}`);
      return;
    }
    if (ssoStatus === 'success' && ssoCode) {
      let cancelled = false;
      (async () => {
        setIsLoading(true);
        try {
          const res = await fetch('/api/sso/exchange', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: ssoCode }),
          });
          const json = await res.json().catch(() => ({}));
          const token = json?.data?.token;
          if (!res.ok || !token) {
            throw new Error(json?.message || 'SSO session expired. Sign in again.');
          }
          await establishSessionFromToken(token, json.data?.refreshToken || undefined);
          if (cancelled) return;
          toast.success('Signed in with SSO');
          navigate('/dashboard', { replace: true });
        } catch (err: any) {
          if (cancelled) return;
          const msg = err?.message || 'SSO login failed';
          setError(msg);
          toast.error(msg);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
  }, [searchParams, navigate, establishSessionFromToken]);

  const handleSsoLogin = async () => {
    if (!tenant) {
      setError('Add ?tenant=your-subdomain to the login URL to use SSO');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`/api/sso/start?subdomain=${encodeURIComponent(tenant)}`);
      const json = await res.json();
      if (!res.ok || !json.data?.authorizationUrl) {
        throw new Error(json.message || 'SSO is not available for this firm');
      }
      window.location.href = json.data.authorizationUrl;
    } catch (err: any) {
      setError(err.message || 'SSO start failed');
      toast.error(err.message || 'SSO start failed');
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password, tenant || undefined);

      if (result.status === 'requires2FA') {
        setTempToken(result.tempToken);
        setStep('2fa');
        toast('Enter your authenticator code');
        return;
      }

      if (result.status === 'requires2FASetup') {
        setTempToken(result.tempToken);
        const setup = await start2FASetup(result.tempToken);
        setQrCode(setup.qrCode);
        setManualKey(setup.manualEntryKey);
        setStep('2fa_setup');
        toast('Admin accounts must enable two-factor authentication');
        return;
      }

      if (result.status === 'requiresPasswordChange') {
        setStep('password_change');
        toast('Please set a new password to continue');
        return;
      }

      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Login failed';
      setError(errorMessage);
      toast.error(errorMessage);
      if (err?.code === 'USE_PORTAL_LOGIN' || /client portal/i.test(errorMessage)) {
        navigate(`/portal/login?email=${encodeURIComponent(formData.email)}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await verify2FA(tempToken, otpCode.trim());
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err?.message || 'Invalid verification code';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const result = await complete2FASetup(tempToken, otpCode.trim());
      setBackupCodes(result.backupCodes);
      setStep('backup_codes');
      toast.success('Two-factor authentication enabled');
    } catch (err: any) {
      const errorMessage = err?.message || 'Setup verification failed';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }
      const strength = evaluatePassword(newPassword);
      if (!strength.valid) {
        throw new Error(`Password requirements not met: ${strength.errors.join(', ')}`);
      }
      await changeFirstPassword(newPassword);
      toast.success('Password updated successfully');
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err?.message || 'Password change failed';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const resetToCredentials = () => {
    setStep('credentials');
    setTempToken('');
    setOtpCode('');
    setQrCode('');
    setManualKey('');
    setBackupCodes([]);
    setError('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: step === 'backup_codes' ? 480 : 400,
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            padding: 3,
            textAlign: 'center'
          }}
        >
          {step === 'credentials' ? (
            <LockOutlined sx={{ fontSize: 48, mb: 2 }} />
          ) : (
            <Security sx={{ fontSize: 48, mb: 2 }} />
          )}
          <Typography variant="h4" component="h1" gutterBottom>
            Legal Dashboard
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {step === 'credentials' && 'Sign in to your account'}
            {step === '2fa' && 'Two-factor authentication'}
            {step === '2fa_setup' && 'Set up two-factor authentication'}
            {step === 'backup_codes' && 'Save your backup codes'}
            {step === 'password_change' && 'Set a new password'}
          </Typography>
          {tenant && step === 'credentials' && (
            <Box sx={{ mt: 2 }}>
              <Chip
                icon={<Business />}
                label={`${tenant.charAt(0).toUpperCase() + tenant.slice(1)} Law Firm`}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            </Box>
          )}
        </Box>

        <CardContent sx={{ padding: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {step === 'credentials' && (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                required
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                }}
                disabled={isLoading}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <Button
                      onClick={handleTogglePassword}
                      sx={{ minWidth: 'auto', p: 0.5 }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  )
                }}
                disabled={isLoading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  mt: 3,
                  mb: 2,
                  height: 48,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                  }
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>

              {tenant && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Business />}
                  onClick={handleSsoLogin}
                  disabled={isLoading}
                  sx={{ mt: 1.5 }}
                >
                  Sign in with SSO
                </Button>
              )}

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have an account? Contact your administrator.
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <Link component={RouterLink} to="/forgot-password" underline="hover">
                    Forgot your password?
                  </Link>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <Link component={RouterLink} to="/portal/login" underline="hover">
                    Client? Sign in at the client portal
                  </Link>
                </Typography>
              </Box>
            </form>
          )}

          {step === '2fa' && (
            <form onSubmit={handleVerify2FA}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the 6-digit code from your authenticator app, or an 8-character backup code.
              </Typography>
              <TextField
                fullWidth
                label="Authentication code"
                value={otpCode}
                onChange={(e) => {
                  setOtpCode(e.target.value);
                  setError('');
                }}
                margin="normal"
                required
                autoFocus
                inputProps={{ maxLength: 8, inputMode: 'text', autoComplete: 'one-time-code' }}
                disabled={isLoading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || otpCode.trim().length < 6}
                sx={{ mt: 3, mb: 1, height: 48 }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
              </Button>
              <Button fullWidth onClick={resetToCredentials} disabled={isLoading}>
                Back to sign in
              </Button>
            </form>
          )}

          {step === '2fa_setup' && (
            <form onSubmit={handleCompleteSetup}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Your organization requires two-factor authentication for admin accounts.
              </Alert>
              {qrCode && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img src={qrCode} alt="2FA QR code" style={{ width: 180, height: 180 }} />
                </Box>
              )}
              {manualKey && (
                <Typography variant="caption" display="block" sx={{ mb: 2, wordBreak: 'break-all' }}>
                  Manual key: {manualKey}
                </Typography>
              )}
              <TextField
                fullWidth
                label="6-digit verification code"
                value={otpCode}
                onChange={(e) => {
                  setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setError('');
                }}
                margin="normal"
                required
                autoFocus
                inputProps={{ maxLength: 6, inputMode: 'numeric', autoComplete: 'one-time-code' }}
                disabled={isLoading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || otpCode.length !== 6}
                sx={{ mt: 3, mb: 1, height: 48 }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Enable & Continue'}
              </Button>
              <Button fullWidth onClick={resetToCredentials} disabled={isLoading}>
                Back to sign in
              </Button>
            </form>
          )}

          {step === 'password_change' && (
            <form onSubmit={handlePasswordChange}>
              <Alert severity="info" sx={{ mb: 2 }}>
                You must set a new permanent password before accessing the dashboard.
              </Alert>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError('');
                }}
                margin="normal"
                required
                autoFocus
                disabled={isLoading}
                helperText="Min 12 characters with upper, lower, number, and special character"
              />
              <PasswordStrengthMeter password={newPassword} />
              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                margin="normal"
                required
                disabled={isLoading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || !newPassword || !confirmPassword}
                sx={{ mt: 3, mb: 1, height: 48 }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Set Password & Continue'}
              </Button>
            </form>
          )}

          {step === 'backup_codes' && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Save these backup codes now. Each can be used once if you lose access to your authenticator.
              </Alert>
              <Box
                sx={{
                  bgcolor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  mb: 2,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                {backupCodes.map((code) => (
                  <Typography key={code} variant="body2">{code}</Typography>
                ))}
              </Box>
              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ height: 48 }}
                onClick={() => {
                  toast.success('Login successful!');
                  navigate('/dashboard');
                }}
              >
                Continue to dashboard
              </Button>
            </Box>
          )}
        </CardContent>
      </Paper>
    </Box>
  );
};

export default LoginPage;
